/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — "core has changed" (Masters Ladder R3 §2.3).

   An override shadows the office master's words. When the master moves
   on, the variant keeps its version — but it must be TOLD, and the
   telling has to be one click from the block, not a trip to a report.
   So: a thin indicator on the block, and this compact side-by-side
   anchored to it. Two answers, both one click: take the new core text,
   or keep this variant's wording (which re-pins the override to the
   core as it stands now and clears the indicator).

   Deliberately a popover, not a full-screen modal — the surrounding
   paragraphs are half the context.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { appLayerProps } from '../components/ui/app-layer';

export interface VariantStaleCompareProps {
  core: string;
  variant: string;
  clientName: string;
  anchor: { left: number; bottom: number };
  onRevert: () => void;
  onKeep: () => void;
  onClose: () => void;
}

export function VariantStaleCompare({
  core,
  variant,
  clientName,
  anchor,
  onRevert,
  onKeep,
  onClose,
}: VariantStaleCompareProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  React.useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const width = 520;
  const left = Math.min(Math.max(8, anchor.left - width / 2), window.innerWidth - width - 8);
  const top = Math.min(anchor.bottom + 6, Math.max(8, window.innerHeight - 280));

  return createPortal(
    <div
      {...appLayerProps}
      ref={ref}
      className="dsg-menu sps-stale-pop"
      role="dialog"
      aria-label="The office master has changed"
      style={{ position: 'fixed', left, top, width }}
    >
      <div className="sps-menu-head" aria-hidden="true">
        The office master has changed
      </div>
      <div className="sps-stale-cols">
        <div className="sps-stale-col">
          <h4 className="sps-stale-col-head">Office master now</h4>
          <p className="sps-stale-text">{core || <em>(empty)</em>}</p>
        </div>
        <div className="sps-stale-col sps-stale-col-mine">
          <h4 className="sps-stale-col-head">{clientName}</h4>
          <p className="sps-stale-text">{variant || <em>(empty)</em>}</p>
        </div>
      </div>
      <div className="sps-variant-pop-actions">
        <button type="button" className="dsg-btn" onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className="dsg-btn"
          onClick={onRevert}
          title="Drop this variant's version and take the office master text"
        >
          Take the core text
        </button>
        <button
          type="button"
          className="dsg-primary"
          onClick={onKeep}
          title="Keep this variant's wording and stop flagging it"
        >
          Keep {clientName}&rsquo;s
        </button>
      </div>
    </div>,
    document.body
  );
}
